import React, { useState, useEffect, useRef } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useDispatch, useSelector } from "react-redux";
import { store } from "../../store";
import { useNavigate } from "react-router-dom";
import { enterInstitute, updateSelectedInstitute, setInstituteSwitching } from "../../store/slices/instituteContextSlice";
import { setUser } from "../../store/slices/authSlice";
import { fetchMyPermissions } from "../../store/slices/permissionsSlice";
import { Formik, Form, Field, FormikProps, FormikHelpers } from "formik";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import instituteService from "../../services/instituteService";
import profileService from "../../services/profileService";
import { useQueryClient } from "@tanstack/react-query";
import { applyWebSessionTokens } from "../../utils/sessionRevocation";
// Google Maps script is loaded by MapLocationPicker when needed
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Switch,
  Tooltip,
  Alert,
  CircularProgress,
  Snackbar,
  FormHelperText,
  Divider,
  Paper,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Add,
  Edit,
  Delete,
  Search,
  Visibility,
  Close,
  Download,
  Upload,
  Business,
  LocationOn,
  Email,
  Phone,
  Login,
  Language,
  CheckCircle,
} from "@mui/icons-material";
import {
  useInstitutes,
  useCreateInstitute,
  useUpdateInstitute,
  useDeleteInstitute,
  useUpdateInstituteStatus,
  useExportInstitutes,
} from "../../hooks/queries/useInstitutes";
import { instituteValidationSchema } from "../../validations/instituteValidation";
import MapLocationPicker, { LocationDetails } from "../../components/common/MapLocationPicker";
import DataImportWizard from "../../components/common/DataImportWizard";
import PermissionGate from "../../components/common/PermissionGate";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyBvJpMkcmHP3YsK3En2HwqpfxOCeL6RoEo";
const libraries: ("places")[] = ["places"];

// Type definitions
interface Institute {
  id: string;
  name: string;
  email: string;
  phoneNumber: string;
  website?: string;
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  pinCode?: string;
  latitude?: number | null;
  longitude?: number | null;
  profileImage?: File | string | null;
  instituteCode?: string;
  status: boolean;
  canDelete?: boolean;
  address?: string;
  instituteDistanceRadius?: number;
  teacherClassDistanceRadius?: number;
  allowTeacherConcurrentSessions?: boolean;
  notifyStudentsOnCrossDaySwap?: boolean;
  notifyStudentsOnSameDaySwap?: boolean;
  notifyStudentsOnExtend?: boolean;
  gfmWhatsappEnabled?: boolean;
  attendanceTokenRefreshSeconds?: number | null;
}

interface FormValues {
  name: string;
  email: string;
  phoneNumber: string;
  website: string;
  street: string;
  city: string;
  state: string;
  country: string;
  pinCode: string;
  latitude: number | null;
  longitude: number | null;
  profileImage: File | null;
  instituteDistanceRadius: number;
  teacherClassDistanceRadius: number;
  allowTeacherConcurrentSessions: boolean;
  notifyStudentsOnCrossDaySwap: boolean;
  notifyStudentsOnSameDaySwap: boolean;
  notifyStudentsOnExtend: boolean;
  gfmWhatsappEnabled: boolean;
  attendanceTokenRefreshSeconds: number | string | null;
}

interface GoogleMapsWindow extends Window {
  google?: {
    maps: {
      places: {
        Autocomplete: new (input: HTMLInputElement, options?: google.maps.places.AutocompleteOptions) => google.maps.places.Autocomplete;
      };
      event: {
        clearInstanceListeners: (instance: google.maps.places.Autocomplete) => void;
      };
    };
  };
}

type MapPickerMode = "create" | "edit";
type ToastSeverity = "success" | "error" | "warning" | "info";

const Institutes: React.FC = () => {
  const dispatch = useDispatch<typeof store.dispatch>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useSelector((state: any) => state.auth.user);
  
  // Helper function to get profile image URL
  const getProfileImageUrl = (profileImage: string | File | null | undefined) => {
    if (!profileImage) return undefined;
    if (typeof profileImage === 'object') return undefined; // File objects are handled elsewhere
    if (typeof profileImage !== 'string') return undefined;
    return profileImage.startsWith('http') ? profileImage : `http://localhost:3001/${profileImage}`;
  };
  
  // Helper function to get clean filename from URL
  const getProfileImageFilename = (profileImage: string | File | null | undefined) => {
    if (!profileImage) return 'Existing image';
    if (typeof profileImage === 'object') return (profileImage as File).name; // File object
    if (typeof profileImage !== 'string') return 'Existing image';
    const filename = profileImage.split('/').pop() || 'Existing image';
    return filename.split('?')[0];
  };
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  const [selectedInstitute, setSelectedInstitute] = useState<Institute | null>(null);
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastSeverity, setToastSeverity] = useState<ToastSeverity>("success");
  const [mapPickerOpen, setMapPickerOpen] = useState<boolean>(false);
  const [mapPickerMode, setMapPickerMode] = useState<MapPickerMode>("create");
  const [currentStreetAddress, setCurrentStreetAddress] = useState<string>("");
  const [importDialogOpen, setImportDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeletingInstitute, setIsDeletingInstitute] = useState<boolean>(false);
  const [togglingInstituteId, setTogglingInstituteId] = useState<string | null>(null);
  const [optimisticStatus, setOptimisticStatus] = useState<Record<string, boolean>>({});

  // Debug effect to log state changes
  useEffect(() => {
    console.log("🟡 [DEBUG] State changed:");
    console.log("🟡 [DEBUG] - mapPickerOpen:", mapPickerOpen);
    console.log("🟡 [DEBUG] - mapPickerMode:", mapPickerMode);
    console.log("🟡 [DEBUG] - currentStreetAddress:", currentStreetAddress);
    console.log("🟡 [DEBUG] - MapLocationPicker should render with open =", mapPickerOpen);
  }, [mapPickerOpen, mapPickerMode, currentStreetAddress]);
  
  const createAddressInputRef = useRef<HTMLInputElement | null>(null);
  const editAddressInputRef = useRef<HTMLInputElement | null>(null);
  const createFormRef = useRef<HTMLFormElement | null>(null);
  const editFormRef = useRef<HTMLFormElement | null>(null);
  const createSetFieldValueRef = useRef<((field: string, value: any) => void) | null>(null);
  const editSetFieldValueRef = useRef<((field: string, value: any) => void) | null>(null);

  const { data: institutesData, isLoading, error } = useInstitutes({
    search: searchQuery,
    page,
    limit: pageSize,
  });

  useEffect(() => {
    console.log("[Institutes] hook state:", {
      isLoading,
      error: error ? (error as any)?.message || error : null,
      institutesData,
      page,
      pageSize,
      searchQuery,
    });
  }, [isLoading, error, institutesData, page, pageSize, searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);
  
  const createMutation = useCreateInstitute() as any;
  const updateMutation = useUpdateInstitute() as any;
  const deleteMutation = useDeleteInstitute() as any;
  const updateStatusMutation = useUpdateInstituteStatus() as any;
  const exportMutation = useExportInstitutes() as any;

  const institutes: Institute[] = Array.isArray(institutesData?.data)
    ? institutesData.data
    : institutesData?.data?.institutes || [];
  const pagination = institutesData?.pagination || {};

  useEffect(() => {
    let autocompleteInstance: google.maps.places.Autocomplete | null = null;
    let focusHandler: (() => void) | null = null;
    let inputHandler: (() => void) | null = null;
    let scrollHandler: ((e: Event) => void) | null = null;
    let timer: NodeJS.Timeout | null = null;

    const googleWindow = window as unknown as GoogleMapsWindow;
    if (createDialogOpen && googleWindow.google?.maps?.places) {
      timer = setTimeout(() => {
        const inputElement = createAddressInputRef.current;
        if (inputElement) {
          const existingPacContainers = document.querySelectorAll('.pac-container');
          existingPacContainers.forEach(container => container.remove());
          
          autocompleteInstance = new googleWindow.google.maps.places.Autocomplete(
            inputElement,
            {
              types: ['geocode', 'establishment'],
              fields: ['address_components', 'geometry', 'formatted_address'],
            }
          );

          autocompleteInstance.addListener('place_changed', () => {
            const place = autocompleteInstance?.getPlace();
            if (place?.geometry && createSetFieldValueRef.current) {
              handlePlaceSelect(place, createSetFieldValueRef.current);
            }
          });

          const setAutocompleteStyle = () => {
            setTimeout(() => {
              const pacContainer = document.querySelector('.pac-container');
              if (pacContainer) {
                (pacContainer as HTMLElement).style.zIndex = '9999';
                (pacContainer as HTMLElement).style.position = 'fixed';
                if (inputElement) {
                  const rect = inputElement.getBoundingClientRect();
                  (pacContainer as HTMLElement).style.width = `${rect.width}px`;
                  (pacContainer as HTMLElement).style.left = `${rect.left}px`;
                  (pacContainer as HTMLElement).style.top = `${rect.bottom}px`;
                }
              }
            }, 50);
          };

          focusHandler = () => setAutocompleteStyle();
          inputHandler = () => setAutocompleteStyle();
          scrollHandler = () => setAutocompleteStyle();

          inputElement.addEventListener('focus', focusHandler);
          inputElement.addEventListener('input', inputHandler);
          window.addEventListener('scroll', scrollHandler, true);
          
          setAutocompleteStyle();
        }
      }, 500);

      return () => {
        if (timer) clearTimeout(timer);
        const inputElement = createAddressInputRef.current;
        if (inputElement) {
          if (focusHandler) inputElement.removeEventListener('focus', focusHandler);
          if (inputHandler) inputElement.removeEventListener('input', inputHandler);
        }
        if (scrollHandler) window.removeEventListener('scroll', scrollHandler, true);
        if (autocompleteInstance && googleWindow.google?.maps?.event) {
          googleWindow.google.maps.event.clearInstanceListeners(autocompleteInstance);
        }
      };
    }
  }, [createDialogOpen]);

  useEffect(() => {
    let autocompleteInstance: google.maps.places.Autocomplete | null = null;
    let focusHandler: (() => void) | null = null;
    let inputHandler: (() => void) | null = null;
    let scrollHandler: ((e: Event) => void) | null = null;
    let timer: NodeJS.Timeout | null = null;

    const googleWindow = window as unknown as GoogleMapsWindow;
    if (editDialogOpen && googleWindow.google?.maps?.places) {
      timer = setTimeout(() => {
        const inputElement = editAddressInputRef.current;
        if (inputElement) {
          // Clear any existing pac-container
          const existingPacContainers = document.querySelectorAll('.pac-container');
          existingPacContainers.forEach(container => container.remove());
          
          autocompleteInstance = new googleWindow.google.maps.places.Autocomplete(
            inputElement,
            {
              types: ['geocode', 'establishment'],
              fields: ['address_components', 'geometry', 'formatted_address'],
            }
          );

          autocompleteInstance.addListener('place_changed', () => {
            const place = autocompleteInstance?.getPlace();
            if (place?.geometry && editSetFieldValueRef.current) {
              handlePlaceSelect(place, editSetFieldValueRef.current);
            }
          });

          const setAutocompleteStyle = () => {
            setTimeout(() => {
              const pacContainer = document.querySelector('.pac-container');
              if (pacContainer) {
                (pacContainer as HTMLElement).style.zIndex = '9999';
                (pacContainer as HTMLElement).style.position = 'fixed';
                if (inputElement) {
                  const rect = inputElement.getBoundingClientRect();
                  (pacContainer as HTMLElement).style.width = `${rect.width}px`;
                  (pacContainer as HTMLElement).style.left = `${rect.left}px`;
                  (pacContainer as HTMLElement).style.top = `${rect.bottom}px`;
                }
              }
            }, 50);
          };

          focusHandler = () => setAutocompleteStyle();
          inputHandler = () => setAutocompleteStyle();
          scrollHandler = () => setAutocompleteStyle();

          inputElement.addEventListener('focus', focusHandler);
          inputElement.addEventListener('input', inputHandler);
          window.addEventListener('scroll', scrollHandler, true);
          
          setAutocompleteStyle();
        }
      }, 500);

      return () => {
        if (timer) clearTimeout(timer);
        const inputElement = editAddressInputRef.current;
        if (inputElement) {
          if (focusHandler) inputElement.removeEventListener('focus', focusHandler);
          if (inputHandler) inputElement.removeEventListener('input', inputHandler);
        }
        if (scrollHandler) window.removeEventListener('scroll', scrollHandler, true);
        if (autocompleteInstance && googleWindow.google?.maps?.event) {
          googleWindow.google.maps.event.clearInstanceListeners(autocompleteInstance);
        }
      };
    }
  }, [editDialogOpen]);

  const showToast = (message: string, severity: ToastSeverity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
  };

  const handleCloseToast = () => {
    setToastMessage(null);
  };

  const initialFormValues: FormValues = {
    name: "",
    email: "",
    phoneNumber: "",
    website: "",
    street: "",
    city: "",
    state: "",
    country: "",
    pinCode: "",
    latitude: null,
    longitude: null,
    profileImage: null,
    instituteDistanceRadius: 500,
    teacherClassDistanceRadius: 200,
    allowTeacherConcurrentSessions: false,
    notifyStudentsOnCrossDaySwap: true,
    notifyStudentsOnSameDaySwap: false,
    notifyStudentsOnExtend: false,
    gfmWhatsappEnabled: true,
    attendanceTokenRefreshSeconds: "",
  };

  const handleCreateInstitute = async (
    values: FormValues,
    { setSubmitting, resetForm }: FormikHelpers<FormValues>
  ) => {
    try {
      await createMutation.mutateAsync(values);
      showToast("Institute Create Successfully", "success");
      setCreateDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Create failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateInstitute = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    if (!selectedInstitute) return;
    try {
      await updateMutation.mutateAsync({
        id: selectedInstitute.id,
        instituteData: values,
      });
      showToast("Institute Update Successfully", "success");
      setEditDialogOpen(false);
      setSelectedInstitute(null);
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteInstitute = async (id: string) => {
    // Open confirmation dialog instead of browser confirm
    setDeleteTargetId(id);
    setDeleteDialogOpen(true);
  };

  const handleToggleStatus = async (institute: Institute) => {
    if (!institute?.id) return;
    const id = institute.id;
    const newStatus = !institute.status;
    setTogglingInstituteId(id);
    setOptimisticStatus((prev) => ({ ...prev, [id]: newStatus }));
    try {
      await updateStatusMutation.mutateAsync({
        id,
        status: newStatus,
      });
      showToast(
        newStatus ? "Institute activated successfully." : "Institute deactivated successfully.",
        "success"
      );
    } catch (error: any) {
      console.error("Status update failed:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update institute status. Please try again.";
      showToast(errorMessage, "error");
      setOptimisticStatus((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } finally {
      setTogglingInstituteId(null);
      setOptimisticStatus((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const handleExportInstitutes = async () => {
    try {
      const response = await exportMutation.mutateAsync({
        search: searchQuery,
      });

      const blob = new Blob([response.csvData], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = response.fileName || `institutes_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast(`Successfully exported ${response.totalRecords} institutes to CSV!`, "success");
    } catch (error) {
      console.error("Export failed:", error);
      showToast("Failed to export institutes. Please try again.", "error");
    }
  };

  const handlePlaceSelect = (
    place: google.maps.places.PlaceResult,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (!place) return;
    
    if (place.geometry?.location) {
      setFieldValue("latitude", place.geometry.location.lat());
      setFieldValue("longitude", place.geometry.location.lng());
    }

    if (place.formatted_address) {
      setFieldValue("street", place.formatted_address);
    }

    place.address_components?.forEach((component) => {
      const types = component.types;
      if (types.includes("locality")) {
        setFieldValue("city", component.long_name);
      }
      if (types.includes("administrative_area_level_1")) {
        setFieldValue("state", component.long_name);
      }
      if (types.includes("country")) {
        setFieldValue("country", component.long_name);
      }
      if (types.includes("postal_code")) {
        setFieldValue("pinCode", component.long_name);
      }
    });
  };

  const handleMapLocationSelect = (
    location: LocationDetails,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (!location || !setFieldValue) return;
    
    // Set coordinates
    setFieldValue("latitude", location.lat);
    setFieldValue("longitude", location.lng);
    
    // Set full address
    if (location.formattedAddress) {
      setFieldValue("street", location.formattedAddress);
    }
    
    // Set address components
    const components = location.addressComponents || {};
    if (components.city) setFieldValue("city", components.city);
    if (components.state) setFieldValue("state", components.state);
    if (components.country) setFieldValue("country", components.country);
    if (components.postalCode) setFieldValue("pinCode", components.postalCode);
    
    // Build street address from components
    if (components.streetNumber || components.route) {
      const streetParts: string[] = [];
      if (components.streetNumber) streetParts.push(components.streetNumber);
      if (components.route) streetParts.push(components.route);
      if (streetParts.length > 0) {
        setFieldValue("street", streetParts.join(" "));
      }
    }
  };

  const handleOpenMapPicker = (mode: MapPickerMode, currentAddress: string = "") => {
    console.log("🔵 [DEBUG] handleOpenMapPicker called");
    console.log("🔵 [DEBUG] Mode:", mode);
    console.log("🔵 [DEBUG] Current Address:", currentAddress);
    console.log("🔵 [DEBUG] Current mapPickerOpen state:", mapPickerOpen);
    
    try {
      // Force state change by toggling if already open
      if (mapPickerOpen) {
        console.log("🔵 [DEBUG] mapPickerOpen is already true, closing first...");
        setMapPickerOpen(false);
        // Use setTimeout to ensure state update completes before opening again
        setTimeout(() => {
          console.log("🔵 [DEBUG] Re-opening map picker...");
          setMapPickerMode(mode);
          setCurrentStreetAddress(currentAddress);
          setMapPickerOpen(true);
          console.log("🔵 [DEBUG] State updated - mapPickerOpen set to true");
        }, 100);
      } else {
        setMapPickerMode(mode);
        setCurrentStreetAddress(currentAddress);
        setMapPickerOpen(true);
        console.log("🔵 [DEBUG] State updated - mapPickerOpen set to true");
      }
      
      console.log("🔵 [DEBUG] mapPickerMode set to:", mode);
      console.log("🔵 [DEBUG] currentStreetAddress set to:", currentAddress);
    } catch (error) {
      console.error("🔴 [ERROR] Error in handleOpenMapPicker:", error);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Institute",
      flex: 1.5,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams<Institute>) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar 
            src={getProfileImageUrl(params.row.profileImage)}
            sx={{ bgcolor: "primary.main", width: 40, height: 40 }}
          >
            <Business />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.instituteCode} • {params.row.city || "N/A"}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.2,
      minWidth: 250,
    },
    {
      field: "phoneNumber",
      headerName: "Phone",
      width: 150,
    },
    {
      field: "city",
      headerName: "City",
      width: 120,
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Institute>) => {
        const rowId = params.row?.id;
        if (rowId == null) return null;
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
          <Switch
            key={rowId}
            checked={optimisticStatus[rowId] ?? Boolean(params.row.status)}
            size="small"
            onChange={() => handleToggleStatus(params.row)}
            disabled={togglingInstituteId === rowId}
          />
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<Institute>) => {
        const rowId = params.row?.id;
        const isActive = rowId != null ? (optimisticStatus[rowId] ?? Boolean(params.row.status)) : false;
        return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title={isActive ? "Manage Institute" : "Institute is inactive"}>
            <span style={{ display: "inline-flex", lineHeight: 0 }}>
            <IconButton
              size="small"
              color="primary"
              disabled={!isActive}
              onClick={async () => {
                dispatch(
                  setInstituteSwitching({
                    active: true,
                    message: `Opening ${params.row.name || "institute"} workspace...`,
                  })
                );
                try {
                  // Store current superAdmin id for switching back
                  localStorage.setItem("originalSuperAdminId", String(user.id));
                  const response = await instituteService.switchToInstitute(params.row.id);
                  const { token, refreshToken } = response.data.data;
                  // New session for the same user: store tokens and notify the socket
                  // (myams:session-changed) so it re-handshakes into the new session room.
                  applyWebSessionTokens({ accessToken: token, refreshToken: refreshToken || null });
                  
                  // Decode the new token to update user in localStorage
                  const tokenParts = token.split('.');
                  if (tokenParts.length === 3) {
                    const payload = JSON.parse(atob(tokenParts[1]));
                    const rawRole = payload.role || payload.userRole || payload.roleName;
                    const normalizedRole =
                      rawRole === "superAdmin" || rawRole === "superadmin"
                        ? "superadmin"
                        : rawRole === "institute"
                          ? "admin"
                          : rawRole;
                    
                    const newUser = {
                      ...payload,
                      id: payload.id || payload.userId || payload.sub,
                      email: payload.email,
                      role: normalizedRole,
                      name: payload.name || payload.username || payload.fullName || user.name,
                      firstName: payload.firstName || payload.first_name || user.firstName,
                      lastName: payload.lastName || payload.last_name || user.lastName,
                      consentFlag: payload.consentFlag ?? payload.consent_flag ?? user.consentFlag ?? 1,
                      acceptedPrivacyPolicyId:
                        payload.acceptedPrivacyPolicyId ||
                        payload.accepted_privacy_policy_id ||
                        user.acceptedPrivacyPolicyId ||
                        null,
                      instituteId: payload.operatingInstituteId || payload.instituteId || params.row.id,
                      operatingInstituteId: payload.operatingInstituteId || params.row.id,
                      isManagingInstitute: true,
                    };
                    
                    localStorage.setItem("user", JSON.stringify(newUser));
                    
                    // Update Redux user state
                    dispatch(setUser(newUser));
                  }
                  
                  // Refresh permissions for the new institute context
                  dispatch(fetchMyPermissions());
                  
                  // Clear query cache to avoid stale data
                  queryClient.clear();
                  // Dispatch enterInstitute for any frontend state if needed
                  dispatch(enterInstitute(params.row));
                  // When superadmin, fetch institute details so sidebar/header/profile use institute data immediately
                  if (user?.role?.toLowerCase() === "superadmin") {
                    try {
                      const detailsRes = await profileService.getInstituteDetails();
                      const raw =
                        detailsRes?.data?.data ??
                        detailsRes?.data?.user ??
                        detailsRes?.data ??
                        detailsRes;
                      if (raw && typeof raw === "object") {
                        const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
                        const institutePayload = {
                          name: raw.name ?? raw.instituteName ?? params.row.name,
                          instituteName: raw.name ?? raw.instituteName ?? params.row.name,
                          email: raw.email ?? currentUser.email,
                          phoneNumber: raw.phoneNumber ?? raw.phone ?? currentUser.phoneNumber,
                          profileImage: raw.profileImage ?? raw.profile_image ?? currentUser.profileImage,
                          street: raw.street,
                          city: raw.city,
                          state: raw.state,
                          country: raw.country,
                          pinCode: raw.pinCode ?? raw.pincode,
                          website: raw.website,
                        };
                        const userWithInstituteData = { ...currentUser, ...institutePayload };
                        dispatch(setUser(userWithInstituteData));
                        localStorage.setItem("user", JSON.stringify(userWithInstituteData));
                        dispatch(updateSelectedInstitute(institutePayload));
                      }
                    } catch (e) {
                      // Non-blocking: institute details will load when user visits Profile
                    }
                  }
                  // Navigate to dashboard
                  navigate("/dashboard");
                } catch (error) {
                  console.error("Failed to switch to institute:", error);
                  // Handle error, maybe show toast
                } finally {
                  dispatch(setInstituteSwitching({ active: false }));
                }
              }}
            >
              <Login />
            </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedInstitute(params.row);
                setViewDialogOpen(true);
              }}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedInstitute(params.row);
                setEditDialogOpen(true);
              }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteInstitute(params.row.id)}
              disabled={!params.row.canDelete || deleteMutation.isPending}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
        );
      },
    },
  ];

  return (
    <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load institutes: {(error as Error).message}
          </Alert>
        )}

        {(createMutation.isError || updateMutation.isError || deleteMutation.isError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Operation failed. Please try again.
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Institute Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage all registered institutes
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <PermissionGate permission="institute.export">
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExportInstitutes}
                disabled={exportMutation.isPending || institutes.length === 0}
              >
                {exportMutation.isPending ? "Exporting..." : "Export"}
              </Button>
            </PermissionGate>
            <PermissionGate permission="institute.create">
              <Button
                variant="outlined"
                startIcon={<Upload />}
                onClick={() => setImportDialogOpen(true)}
              >
                Import
              </Button>
            </PermissionGate>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Add Institute
            </Button>
          </Box>
        </Box>

        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[
            {
              label: "Total Institutes",
              value: pagination.total || 0,
              color: "primary.main",
            },
            {
              label: "Active",
              value: institutes.filter((i) => i.status).length,
              color: "success.main",
            },
            {
              label: "Current Page",
              value: pagination.page || 1,
              color: "info.main",
            },
            {
              label: "Total Pages",
              value: pagination.totalPages || 1,
              color: "warning.main",
            },
          ].map((stat, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Card>
                <CardContent sx={{ textAlign: "center", py: 2 }}>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ color: stat.color }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card>
          <CardContent>
            <Box sx={{ mb: 2 }}>
              <TextField
                placeholder="Search institutes..."
                size="small"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <DataGrid
              rows={institutes}
              getRowId={(row) => row.id}
              columns={columns}
              loading={isLoading}
              autoHeight
              disableRowSelectionOnClick
              paginationMode="server"
              rowCount={pagination.total || 0}
              paginationModel={{
                page: page - 1,
                pageSize: pageSize,
              }}
              onPaginationModelChange={(model) => {
                setPage(model.page + 1);
                setPageSize(model.pageSize);
              }}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              sx={{ border: "none" }}
            />
          </CardContent>
        </Card>

        {/* Import Wizard */}
        <DataImportWizard
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          entityType={"institutes"}
          instituteId={null}
          onImportComplete={() => {
            setImportDialogOpen(false);
            // Refresh institutes list
            queryClient.invalidateQueries({ queryKey: ['institutes'] });
            showToast('Import finished. Institutes list refreshed.', 'success');
          }}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; if (!isDeletingInstitute) { setDeleteDialogOpen(false); setDeleteTargetId(null); } }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, color: '#d32f2f' }}>Delete Institute</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography sx={{ mb: 2 }}>
              Are you sure you want to delete this institute? This action cannot be undone.
            </Typography>
            <Alert severity="warning">
              <Typography variant="body2">
                This will also deactivate the associated user account.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => { setDeleteDialogOpen(false); setDeleteTargetId(null); }} 
              disabled={isDeletingInstitute}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={async () => {
                if (!deleteTargetId) return;
                try {
                  setIsDeletingInstitute(true);
                  await deleteMutation.mutateAsync(deleteTargetId);
                  showToast('Institute deleted successfully!', 'success');
                  setDeleteDialogOpen(false);
                  setDeleteTargetId(null);
                  queryClient.invalidateQueries({ queryKey: ['institutes'] });
                } catch (err) {
                  const errorMessage = (err as any)?.response?.data?.message || 'Failed to delete institute. Please try again.';
                  showToast(errorMessage, 'error');
                  console.error('Delete failed:', err);
                } finally {
                  setIsDeletingInstitute(false);
                }
              }} 
              disabled={isDeletingInstitute}
            >
              {isDeletingInstitute ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setViewDialogOpen(false); }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              Institute Details
              <IconButton onClick={() => setViewDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedInstitute && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sx={{ textAlign: "center" }}>
                  <Avatar
                    src={getProfileImageUrl(selectedInstitute.profileImage)}
                    sx={{
                      width: 80,
                      height: 80,
                      mx: "auto",
                      mb: 1,
                      bgcolor: "primary.main",
                    }}
                  >
                    <Business sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h6">{selectedInstitute.name}</Typography>
                  <Chip
                    label={selectedInstitute.instituteCode || "N/A"}
                    size="small"
                    color="primary"
                    sx={{ mt: 1 }}
                  />
                </Grid>
                {[
                  {
                    label: "Email",
                    value: selectedInstitute.email,
                    icon: <Email fontSize="small" />,
                  },
                  {
                    label: "Phone",
                    value: selectedInstitute.phoneNumber,
                    icon: <Phone fontSize="small" />,
                  },
                  {
                    label: "Website",
                    value: selectedInstitute.website || "N/A",
                    icon: <Language fontSize="small" />,
                  },
                  {
                    label: "Address",
                    value: selectedInstitute.address || "N/A",
                    icon: <LocationOn fontSize="small" />,
                  },
                ].map((item, i) => (
                  <Grid item xs={12} key={i}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {item.icon}
                      <Typography variant="body2">{item.value}</Typography>
                    </Box>
                  </Grid>
                ))}
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    City
                  </Typography>
                  <Typography variant="body1">
                    {selectedInstitute.city || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    State
                  </Typography>
                  <Typography variant="body1">
                    {selectedInstitute.state || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Country
                  </Typography>
                  <Typography variant="body1">
                    {selectedInstitute.country || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Pin Code
                  </Typography>
                  <Typography variant="body1">
                    {selectedInstitute.pinCode || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={() => {
                setViewDialogOpen(false);
                setEditDialogOpen(true);
              }}
            >
              Edit
            </Button>
          </DialogActions>
        </Dialog>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setCreateDialogOpen(false); }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            py: 2.5,
            px: 3,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Business sx={{ fontSize: 28 }} />
              <Typography variant="h5" fontWeight={600}>
                Add New Institute
              </Typography>
            </Box>
            <IconButton 
              onClick={() => setCreateDialogOpen(false)}
              sx={{ 
                color: 'white',
                '&:hover': { 
                  bgcolor: 'primary.dark' 
                } 
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Formik
          initialValues={initialFormValues}
          validationSchema={instituteValidationSchema}
          onSubmit={handleCreateInstitute}
        >
          {({ values, errors, touched, setFieldValue, setFieldTouched, isSubmitting }) => {
            // Store setFieldValue in ref for autocomplete to use
            createSetFieldValueRef.current = setFieldValue;
            
            // Check if address data exists
            const hasAddressData = !!(values.street || values.city || values.state || values.country || values.pinCode || values.latitude || values.longitude);
            
            return (
              <Form>
              <DialogContent sx={{ px: 3, py: 3, bgcolor: '#f8f9fa' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                        Basic Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                          <Field
                            as={TextField}
                            fullWidth
                            label="Institute Name *"
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              }
                            }}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={4}>
                          <Field
                            as={TextField}
                            fullWidth
                            label="Website *"
                            name="website"
                            error={touched.website && Boolean(errors.website)}
                            helperText={touched.website && errors.website}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              }
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Field
                            as={TextField}
                            fullWidth
                            label="Email *"
                            name="email"
                            type="email"
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Email color="action" />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              }
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box>
                            <PhoneInput
                              country={"in"}
                              value={values.phoneNumber}
                              onChange={(phone: string) => {
                                setFieldValue("phoneNumber", phone);
                                setFieldTouched("phoneNumber", true);
                              }}
                              onBlur={() => setFieldTouched("phoneNumber", true)}
                              inputStyle={{
                                width: "100%",
                                height: "56px",
                                borderRadius: "8px",
                                border: "1px solid rgba(0, 0, 0, 0.23)",
                                fontSize: "1rem",
                              }}
                              containerStyle={{
                                width: "100%",
                              }}
                              buttonStyle={{
                                borderRadius: "8px 0 0 8px",
                              }}
                              specialLabel="Phone *"
                            />
                            {touched.phoneNumber && errors.phoneNumber && (
                              <FormHelperText error sx={{ ml: 1.75 }}>{errors.phoneNumber}</FormHelperText>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Address Section — always visible; map picker auto-fills fields */}
                  <Grid item xs={12}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 2.5, 
                          bgcolor: 'white',
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: 'primary.main',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <LocationOn color="primary" sx={{ fontSize: 24 }} />
                          <Typography variant="subtitle1" fontWeight={600} color="primary">
                            Address Information
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mb: 2 }}>
                          <TextField
                            fullWidth
                            label="Street Address"
                            name="street"
                            value={values.street || ""}
                            placeholder="No address selected"
                            error={touched.street && Boolean(errors.street)}
                            helperText={touched.street && errors.street}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationOn color="primary" />
                                </InputAdornment>
                              ),
                            }}
                            inputRef={createAddressInputRef}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setFieldValue("street", e.target.value);
                              setCurrentStreetAddress(e.target.value || "");
                            }}
                            sx={{ flex: 1 }}
                          />
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<LocationOn />}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              handleOpenMapPicker("create", values.street || "");
                            }}
                            sx={{ 
                              mt: 1, 
                              minWidth: 150,
                              borderWidth: 2,
                              '&:hover': {
                                borderWidth: 2,
                              }
                            }}
                          >
                            Change
                          </Button>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="City"
                              name="city"
                              error={touched.city && Boolean(errors.city)}
                              helperText={touched.city && errors.city}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="State"
                              name="state"
                              error={touched.state && Boolean(errors.state)}
                              helperText={touched.state && errors.state}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="Country"
                              name="country"
                              error={touched.country && Boolean(errors.country)}
                              helperText={touched.country && errors.country}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="Pin Code"
                              name="pinCode"
                              error={touched.pinCode && Boolean(errors.pinCode)}
                              helperText={touched.pinCode && errors.pinCode}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="Latitude"
                              name="latitude"
                              type="number"
                              error={touched.latitude && Boolean(errors.latitude)}
                              helperText={touched.latitude && errors.latitude}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="Longitude"
                              name="longitude"
                              type="number"
                              error={touched.longitude && Boolean(errors.longitude)}
                              helperText={touched.longitude && errors.longitude}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="Institute Distance Radius (Meters)"
                              name="instituteDistanceRadius"
                              type="number"
                              error={touched.instituteDistanceRadius && Boolean(errors.instituteDistanceRadius)}
                              helperText={touched.instituteDistanceRadius && errors.instituteDistanceRadius}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="Teacher Class Distance Radius (Meters)"
                              name="teacherClassDistanceRadius"
                              type="number"
                              error={touched.teacherClassDistanceRadius && Boolean(errors.teacherClassDistanceRadius)}
                              helperText={touched.teacherClassDistanceRadius && errors.teacherClassDistanceRadius}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                        Profile Image
                      </Typography>
                      <Button 
                        variant="outlined" 
                        component="label" 
                        fullWidth
                        startIcon={<Upload />}
                        sx={{
                          py: 1.5,
                          borderRadius: 2,
                          borderWidth: 2,
                          textTransform: 'none',
                          fontSize: '1rem',
                          '&:hover': {
                            borderWidth: 2,
                          }
                        }}
                      >
                        Upload Profile Image
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const file = e.target.files?.[0] || null;
                            setFieldValue("profileImage", file);
                          }}
                        />
                      </Button>
                      {values.profileImage && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <CheckCircle color="success" fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                              Selected: {typeof values.profileImage === 'object' && values.profileImage instanceof File 
                                ? (values.profileImage as File).name 
                                : 'Existing image'
                              }
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Avatar
                              src={typeof values.profileImage === 'object' && values.profileImage instanceof File
                                ? URL.createObjectURL(values.profileImage as File)
                                : undefined
                              }
                              sx={{ width: 80, height: 80, border: '2px solid #e0e0e0' }}
                              variant="rounded"
                            >
                              <Business />
                            </Avatar>
                          </Box>
                        </Box>
                      )}
                      {touched.profileImage && errors.profileImage && (
                        <FormHelperText error sx={{ mt: 1 }}>{String(errors.profileImage)}</FormHelperText>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
                <Button 
                  onClick={() => setCreateDialogOpen(false)} 
                  disabled={isSubmitting}
                  sx={{
                    textTransform: 'none',
                    px: 3,
                    borderRadius: 2,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Add />}
                  sx={{
                    textTransform: 'none',
                    px: 4,
                    py: 1,
                    borderRadius: 2,
                  }}
                >
                  {isSubmitting ? "Creating..." : "Create Institute"}
                </Button>
              </DialogActions>
            </Form>
            );
          }}
        </Formik>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setEditDialogOpen(false); }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            py: 2.5,
            px: 3,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Edit sx={{ fontSize: 28 }} />
              <Typography variant="h5" fontWeight={600}>
                Edit Institute
              </Typography>
            </Box>
            <IconButton 
              onClick={() => setEditDialogOpen(false)}
              sx={{ 
                color: 'white',
                '&:hover': { 
                  bgcolor: 'primary.dark' 
                } 
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Formik
          initialValues={selectedInstitute ? {
            name: selectedInstitute.name || "",
            email: selectedInstitute.email || "",
            phoneNumber: selectedInstitute.phoneNumber || "",
            website: selectedInstitute.website || "",
            street: selectedInstitute.street || "",
            city: selectedInstitute.city || "",
            state: selectedInstitute.state || "",
            country: selectedInstitute.country || "",
            pinCode: selectedInstitute.pinCode || "",
            latitude: selectedInstitute.latitude || null,
            longitude: selectedInstitute.longitude || null,
            profileImage: selectedInstitute.profileImage || null,
            instituteDistanceRadius: selectedInstitute.instituteDistanceRadius || 500,
            teacherClassDistanceRadius: selectedInstitute.teacherClassDistanceRadius || 200,
            allowTeacherConcurrentSessions: selectedInstitute.allowTeacherConcurrentSessions || false,
            notifyStudentsOnCrossDaySwap: selectedInstitute.notifyStudentsOnCrossDaySwap ?? true,
            notifyStudentsOnSameDaySwap: selectedInstitute.notifyStudentsOnSameDaySwap ?? false,
            notifyStudentsOnExtend: selectedInstitute.notifyStudentsOnExtend ?? false,
            gfmWhatsappEnabled: selectedInstitute.gfmWhatsappEnabled ?? true,
            attendanceTokenRefreshSeconds: selectedInstitute.attendanceTokenRefreshSeconds ?? "",
          } : initialFormValues}
          validationSchema={instituteValidationSchema}
          onSubmit={handleUpdateInstitute}
          enableReinitialize
        >
          {({ values, errors, touched, setFieldValue, setFieldTouched, isSubmitting }) => {
            // Store setFieldValue in ref for autocomplete to use
            editSetFieldValueRef.current = setFieldValue;
            
            // Check if address data exists
            const hasAddressData = !!(values.street || values.city || values.state || values.country || values.pinCode || values.latitude || values.longitude);
            
            return (
              <Form>
              <DialogContent sx={{ px: 3, py: 3, bgcolor: '#f8f9fa' }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                        Basic Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={8}>
                          <Field
                            as={TextField}
                            fullWidth
                            label="Institute Name *"
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              }
                            }}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={4}>
                          <Field
                            as={TextField}
                            fullWidth
                            label="Website *"
                            name="website"
                            error={touched.website && Boolean(errors.website)}
                            helperText={touched.website && errors.website}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              }
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Field
                            as={TextField}
                            fullWidth
                            label="Email *"
                            name="email"
                            type="email"
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Email color="action" />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              }
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Box>
                            <PhoneInput
                              country={"in"}
                              value={values.phoneNumber}
                              onChange={(phone: string) => {
                                setFieldValue("phoneNumber", phone);
                                setFieldTouched("phoneNumber", true);
                              }}
                              onBlur={() => setFieldTouched("phoneNumber", true)}
                              inputStyle={{
                                width: "100%",
                                height: "56px",
                                borderRadius: "8px",
                                border: "1px solid rgba(0, 0, 0, 0.23)",
                                fontSize: "1rem",
                              }}
                              containerStyle={{
                                width: "100%",
                              }}
                              buttonStyle={{
                                borderRadius: "8px 0 0 8px",
                              }}
                              specialLabel="Phone *"
                            />
                            {touched.phoneNumber && errors.phoneNumber && (
                              <FormHelperText error sx={{ ml: 1.75 }}>{errors.phoneNumber}</FormHelperText>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Address Section — always visible; map picker auto-fills fields */}
                  <Grid item xs={12}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 2.5, 
                          bgcolor: 'white',
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: 'primary.main',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <LocationOn color="primary" sx={{ fontSize: 24 }} />
                          <Typography variant="subtitle1" fontWeight={600} color="primary">
                            Address Information
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mb: 2 }}>
                          <TextField
                            fullWidth
                            label="Street Address"
                            name="street"
                            value={values.street || ""}
                            placeholder="No address selected"
                            error={touched.street && Boolean(errors.street)}
                            helperText={touched.street && errors.street}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationOn color="primary" />
                                </InputAdornment>
                              ),
                            }}
                            inputRef={editAddressInputRef}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setFieldValue("street", e.target.value);
                              setCurrentStreetAddress(e.target.value || "");
                            }}
                            sx={{ flex: 1 }}
                          />
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<LocationOn />}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              handleOpenMapPicker("edit", values.street || "");
                            }}
                            sx={{ 
                              mt: 1, 
                              minWidth: 150,
                              borderWidth: 2,
                              '&:hover': {
                                borderWidth: 2,
                              }
                            }}
                          >
                            Change
                          </Button>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="City"
                              name="city"
                              error={touched.city && Boolean(errors.city)}
                              helperText={touched.city && errors.city}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="State"
                              name="state"
                              error={touched.state && Boolean(errors.state)}
                              helperText={touched.state && errors.state}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="Country"
                              name="country"
                              error={touched.country && Boolean(errors.country)}
                              helperText={touched.country && errors.country}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="Pin Code"
                              name="pinCode"
                              error={touched.pinCode && Boolean(errors.pinCode)}
                              helperText={touched.pinCode && errors.pinCode}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="Latitude"
                              name="latitude"
                              type="number"
                              error={touched.latitude && Boolean(errors.latitude)}
                              helperText={touched.latitude && errors.latitude}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="Longitude"
                              name="longitude"
                              type="number"
                              error={touched.longitude && Boolean(errors.longitude)}
                              helperText={touched.longitude && errors.longitude}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="Institute Distance Radius (Meters)"
                              name="instituteDistanceRadius"
                              type="number"
                              error={touched.instituteDistanceRadius && Boolean(errors.instituteDistanceRadius)}
                              helperText={touched.instituteDistanceRadius && errors.instituteDistanceRadius}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Field
                              as={TextField}
                              fullWidth
                              label="Teacher Class Distance Radius (Meters)"
                              name="teacherClassDistanceRadius"
                              type="number"
                              error={touched.teacherClassDistanceRadius && Boolean(errors.teacherClassDistanceRadius)}
                              helperText={touched.teacherClassDistanceRadius && errors.teacherClassDistanceRadius}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  Allow Teacher Concurrent Sessions
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Allow teachers to have overlapping timetable sessions (e.g., lab batches)
                                </Typography>
                              </Box>
                              <Switch
                                checked={values.allowTeacherConcurrentSessions}
                                onChange={(e) => setFieldValue('allowTeacherConcurrentSessions', e.target.checked)}
                                color="primary"
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="QR/OTP Code Refresh Interval (seconds)"
                              name="attendanceTokenRefreshSeconds"
                              type="number"
                              placeholder="60"
                              value={values.attendanceTokenRefreshSeconds ?? ""}
                              onChange={(e) => setFieldValue('attendanceTokenRefreshSeconds', e.target.value)}
                              onBlur={() => setFieldTouched('attendanceTokenRefreshSeconds', true)}
                              inputProps={{ min: 15, max: 1800, step: 1 }}
                              error={Boolean(touched.attendanceTokenRefreshSeconds && errors.attendanceTokenRefreshSeconds)}
                              helperText={
                                (touched.attendanceTokenRefreshSeconds && errors.attendanceTokenRefreshSeconds)
                                  ? String(errors.attendanceTokenRefreshSeconds)
                                  : "Leave blank to use the system default (60 seconds). Allowed range: 15–1800 seconds."
                              }
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mt: 2, mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
                              Lecture Swap Notifications
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  Notify Students on Cross-Day Swap
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Send push notification to students when a lecture is swapped to a different day
                                </Typography>
                              </Box>
                              <Switch
                                checked={values.notifyStudentsOnCrossDaySwap}
                                onChange={(e) => setFieldValue('notifyStudentsOnCrossDaySwap', e.target.checked)}
                                color="primary"
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  Notify Students on Same-Day Swap
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Send push notification to students when a lecture is swapped within the same day
                                </Typography>
                              </Box>
                              <Switch
                                checked={values.notifyStudentsOnSameDaySwap}
                                onChange={(e) => setFieldValue('notifyStudentsOnSameDaySwap', e.target.checked)}
                                color="primary"
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  Notify Students on Lecture Extension
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Send push notification to students when a lecture is extended into the next slot
                                </Typography>
                              </Box>
                              <Switch
                                checked={values.notifyStudentsOnExtend}
                                onChange={(e) => setFieldValue('notifyStudentsOnExtend', e.target.checked)}
                                color="primary"
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mt: 2, mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
                              Guardian Faculty Reports
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  Send Defaulter Reports over WhatsApp
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  When off, GFM defaulter reports are sent by email only (WhatsApp delivery is disabled for this institute)
                                </Typography>
                              </Box>
                              <Switch
                                checked={values.gfmWhatsappEnabled}
                                onChange={(e) => setFieldValue('gfmWhatsappEnabled', e.target.checked)}
                                color="primary"
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'white', borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                        Profile Image
                      </Typography>
                      
                      {/* Show current image if exists */}
                      {selectedInstitute?.profileImage && !values.profileImage && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <CheckCircle color="info" fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                              Current: {getProfileImageFilename(selectedInstitute.profileImage)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Avatar
                              src={getProfileImageUrl(selectedInstitute.profileImage)}
                              sx={{ width: 80, height: 80, border: '2px solid #90caf9' }}
                              variant="rounded"
                            >
                              <Business />
                            </Avatar>
                          </Box>
                        </Box>
                      )}
                      
                      <Button 
                        variant="outlined" 
                        component="label" 
                        fullWidth
                        startIcon={<Upload />}
                        sx={{
                          py: 1.5,
                          borderRadius: 2,
                          borderWidth: 2,
                          textTransform: 'none',
                          fontSize: '1rem',
                          '&:hover': {
                            borderWidth: 2,
                          }
                        }}
                      >
                        {selectedInstitute?.profileImage ? 'Change Profile Image' : 'Upload Profile Image'}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const file = e.target.files?.[0] || null;
                            setFieldValue("profileImage", file);
                          }}
                        />
                      </Button>
                      
                      {values.profileImage && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <CheckCircle color="success" fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                              {typeof values.profileImage === 'object' && values.profileImage instanceof File 
                                ? (values.profileImage as File).name 
                                : getProfileImageFilename(values.profileImage as string)
                              }
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Avatar
                              src={typeof values.profileImage === 'object' && values.profileImage instanceof File
                                ? URL.createObjectURL(values.profileImage as File)
                                : getProfileImageUrl(values.profileImage as string)
                              }
                              sx={{ width: 80, height: 80, border: '2px solid #4caf50' }}
                              variant="rounded"
                            >
                              <Business />
                            </Avatar>
                          </Box>
                          {selectedInstitute?.profileImage && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: '#fff3e0', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                This will replace the current image
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      {touched.profileImage && errors.profileImage && (
                        <FormHelperText error sx={{ mt: 1 }}>{String(errors.profileImage)}</FormHelperText>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
                <Button 
                  onClick={() => setEditDialogOpen(false)} 
                  disabled={isSubmitting}
                  sx={{
                    textTransform: 'none',
                    px: 3,
                    borderRadius: 2,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Edit />}
                  sx={{
                    textTransform: 'none',
                    px: 4,
                    py: 1,
                    borderRadius: 2,
                  }}
                >
                  {isSubmitting ? "Updating..." : "Update Institute"}
                </Button>
              </DialogActions>
            </Form>
            );
          }}
        </Formik>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={!!toastMessage}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toastSeverity}
          onClose={handleCloseToast}
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>

      {/* Map Location Picker */}
      <MapLocationPicker
        key={`map-picker-${Date.now()}-${mapPickerOpen}`}
        open={mapPickerOpen}
        onClose={() => {
          console.log("🟣 [DEBUG] MapLocationPicker onClose called");
          setMapPickerOpen(false);
        }}
        onConfirm={(location: LocationDetails) => {
          console.log("🟣 [DEBUG] MapLocationPicker onConfirm called");
          console.log("🟣 [DEBUG] Location:", location);
          console.log("🟣 [DEBUG] mapPickerMode:", mapPickerMode);
          console.log("🟣 [DEBUG] createSetFieldValueRef.current:", createSetFieldValueRef.current);
          console.log("🟣 [DEBUG] editSetFieldValueRef.current:", editSetFieldValueRef.current);
          
          if (mapPickerMode === "create" && createSetFieldValueRef.current) {
            console.log("🟣 [DEBUG] Calling handleMapLocationSelect for create mode");
            handleMapLocationSelect(location, createSetFieldValueRef.current);
          } else if (mapPickerMode === "edit" && editSetFieldValueRef.current) {
            console.log("🟣 [DEBUG] Calling handleMapLocationSelect for edit mode");
            handleMapLocationSelect(location, editSetFieldValueRef.current);
          } else {
            console.warn("🟠 [WARN] No setFieldValue ref available or wrong mode");
          }
        }}
        initialAddress={currentStreetAddress}
        apiKey={GOOGLE_MAPS_API_KEY}
      />
      </Box>
  );
};

export default Institutes;

